import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../../../sharedFeatures/services/notification.service';
import { PageTitleService } from '../../../../sharedFeatures/services/page-title.service';
import { LoginService } from '../../services/login.service';

@Component({
  selector: 'app-forget-password',
  templateUrl: './forget-password.component.html',
  styleUrls: ['./forget-password.component.scss'],
})
export class ForgetPasswordComponent implements OnInit, OnDestroy {
  forgetPasswordForm!: FormGroup;
  subscriptions: Subscription[] = [];
  isSubmitting: boolean = false;
  readonly emailErrors = {
    required: 'users.forgetPassword.validation.required',
    email: 'users.forgetPassword.validation.email',
    maxlength: 'users.forgetPassword.validation.maxlength',
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private loginService: LoginService,
    private pageTitle: PageTitleService
  ) {}

  ngOnInit(): void {
    this.setPageTitle();
    this.buildForm();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Submit forget password form
   */
  submit(): void {
    if (this.forgetPasswordForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;

      const email = this.forgetPasswordForm.controls['email'].value;

      this.subscriptions.push(
        this.loginService.forgetPassword(email).subscribe(
          (res: string) => {
            this.isSubmitting = false;

            // Check if result is null or empty
            if (!res || res === null || res.trim() === '') {
              this.notificationService.showErrorTranslated(
                'users.forgetPassword.emailNotFound',
                ''
              );
              return;
            }

            // Show success message
            this.notificationService.showSuccessTranslated(
              'users.forgetPassword.mailSentSuccess',
              ''
            );

            // Clear form
            this.forgetPasswordForm.reset();

            // Redirect to login after short delay
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 2000);
          },
          (error: any) => {
            this.isSubmitting = false;

            // Show error message
            if (error.status === 400 || error.status === 404) {
              this.notificationService.showErrorTranslated(
                'users.forgetPassword.emailNotFound',
                ''
              );
            } else {
              this.notificationService.showErrorTranslated(
                'users.forgetPassword.genericError',
                ''
              );
            }
          }
        )
      );
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.forgetPasswordForm.controls).forEach(control => {
        this.forgetPasswordForm.controls[control].markAsTouched();
      });
    }
  }

  /**
   * Build the forget password form
   */
  buildForm(): void {
    this.forgetPasswordForm = this.fb.group({
      email: [
        null,
        [Validators.required, Validators.email, Validators.maxLength(200)],
      ],
    });
  }

  /**
   * Set page title
   */
  setPageTitle(): void {
    this.pageTitle.setTitleTranslated('User.forgetPassword');
  }

  /**
   * Get form control by name
   */
  getControl(controlName: string): any {
    return this.forgetPasswordForm?.controls[controlName];
  }

  /**
   * Navigate back to login
   */
  goToLogin(): void {
    this.router.navigate(['/login']);
  }
}
